import { Link, Stack, Text } from "@fluentui/react";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { CreateDocumentModal } from "../modals/CreateDocumentModal";
import { useTranslation } from "react-i18next";
import { HorizontalStack, VerticalStack } from "../Stack";
import { CreateProjectModal } from "../modals/CreateProjectModal";
import { useSelector } from "react-redux";
import { colorPallete } from "../../assets/color";
import UploadDocumentIcon from "../../assets/Icons/svg/uploadDocumentIcon";
import DocRejectModal from "../modals/DocRejectionModal";
import useIsSignedIn from "../../hooks/useIsSignedIn";
import { useGlobalDragDrop } from "./useGlobalDragDrop";
import { DragOverlay } from "./DragOverlay";
import "./DragAndDrop.css";

export default function DragAndDrop(props) {
  const { t } = useTranslation();
  const { isModalOpen, showModal, hideModal } = props;
  const [fileName, setFileName] = useState("");
  const [projectModel, setProjectModel] = useState(false);
  const [docRejectModal, setDocRejectModal] = useState(false);
  const [multipleFiles, setMultipleFiles] = useState([]);
  const [newProjectName, setNewProjectName] = useState("");
  const hasAutoOpened = useRef(false);
  const isSignedIn = useIsSignedIn();
  const wrapperRef = useRef(null);


  function addMultipleFiles(files) {
    let incomingFiles = [];
    files.forEach((fObject) => {
      incomingFiles.push({
        fileName: lowercaseAfterLastDot(fObject.name),
        file: fObject,
      });
    });
    setMultipleFiles((prevFiles) => {
      let result = [...prevFiles, ...incomingFiles];
      const uniqueArr = result.filter(
        (value, index, array) =>
          array.findIndex((curr) => value.fileName === curr.fileName) === index
      );
      return uniqueArr;
    });
  }
  const lowercaseAfterLastDot = (text) => {
    const lastDotIndex = text.lastIndexOf(".");
    if (lastDotIndex === -1 || lastDotIndex === text.length - 1) {
      return text; // No dot or dot at the end
    }

    const beforeDot = text.slice(0, lastDotIndex + 1);
    const afterDot = text.slice(lastDotIndex + 1);

    return beforeDot + afterDot.toLowerCase();
  };

  const handleDrop = useCallback((files) => {
    if (files && files.length > 0) {
      setFileName(lowercaseAfterLastDot(files[0].name));
      addMultipleFiles(files);
      showModal();
    }
  }, []);

  const { isDraggingOver } = useGlobalDragDrop(handleDrop);

  const onDrop = useCallback((file) => {
    handleDrop(file);
  }, [handleDrop]);
  useEffect(() => {
    if (
      props.direction === "open" &&
      !hasAutoOpened.current &&
      props.data?.length === 0
    ) {
      hasAutoOpened.current = true;
      showModal();
    }
  }, [props.direction, props.data]);

  const functionalityState = useSelector((state) => state.DocumentStack.value);
  useEffect(() => {}, [functionalityState]);

  function showProjectModal() {
    setProjectModel(true);
  }

  function showDocRejectModal() {
    setDocRejectModal(true);
  }

  function hideProjectModal() {
    setProjectModel(false);
  }

  function hideDocRejectModal() {
    setDocRejectModal(false);
  }
  function getModelValue() {
    hideModal();
    showProjectModal();
  }
  function displayUpload(projectName) {
    setNewProjectName(projectName || "");
    showModal();
  }

  const {
    getRootProps,
    getInputProps,
    isFocused,
    isDragAccept,
    isDragReject,
    acceptedFiles,
  } = useDropzone({ onDrop, disabled: !isSignedIn });

  const getDropzoneClassName = useMemo(() => {
    let className = "dragAndDropBase";
    if (isFocused) className += " dragAndDropFocused";
    if (isDragAccept) className += " dragAndDropAccept";
    if (isDragReject) className += " dragAndDropReject";
    return className;
  }, [isFocused, isDragAccept, isDragReject]);

  const getModal = () => {
    if ((props.data || props.projectId) && !projectModel) {
      return (
        <CreateDocumentModal
          fileName={fileName}
          isModalOpen={isModalOpen}
          showModal={showModal}
          hideModal={hideModal}
          project={getModelValue}
          refresh={props.refresh}
          folderName={newProjectName || props?.folderName}
          projectId={props?.projectId}
          multipleFiles={multipleFiles}
          setMultipleFiles={setMultipleFiles}
          rootDocumentId={props?.rootDocumentId}
          setRootDocumentId={props?.setRootDocumentId}
          onUploadSuccess={props?.onUploadSuccess}
        />
      );
    } else if (!projectModel && !props.direction) {
      return (
        <CreateDocumentModal
          fileName={fileName}
          isModalOpen={isModalOpen}
          showModal={showModal}
          hideModal={hideModal}
          project={getModelValue}
          folderName={newProjectName}
          multipleFiles={multipleFiles}
          setMultipleFiles={setMultipleFiles}
        />
      );
    } else if (projectModel) {
      return (
        <CreateProjectModal
          isModalOpen={projectModel}
          showModal={showProjectModal}
          hideModal={hideProjectModal}
          displayDoc={displayUpload}
        />
      );
    }
  };

  return (
    <VerticalStack className="dragAndDropContainer" title={t("click_to_upload")}>
      {isDraggingOver && <DragOverlay />}
      {/* <Text style={{ marginBottom: "3%" }}>{props.dropZoneText}</Text> */}
      <div {...getRootProps({ className: getDropzoneClassName })} ref={wrapperRef}>
        <input {...getInputProps()} />
        <Stack
          style={{
            justifyContent: props.data ? "space-betweeen" : "center",
            alignItems: "center",
            flexDirection:
              props.data || props.textStackDirection ? "row" : "column",
            height: props.data ? "100%" : "50%",
            backgroundColor: "",
          }}
        >
          <UploadDocumentIcon height="30px" width="27px" />
          <HorizontalStack
            className="dragAndDropDrag"
            style={{ paddingTop: props.data ? "0%" : "2%" }}
          >
            <Text className="dragAndDropText Drag">
              {t("drag_drop")}
            </Text>
            <Link
              style={{
                fontSize: "14px",
                lineHeight: "16px",
                fontWeight: "400",
                color: colorPallete.btBlue,
                fontFamily: "Poppins",
              }}
              underline
            >
              {t("browse")}
            </Link>
          </HorizontalStack>
        </Stack>
      </div>
      {getModal()}
      <DocRejectModal
        isModalOpen={docRejectModal}
        showModal={showDocRejectModal}
        hideModal={hideDocRejectModal}
      />
    </VerticalStack>
  );
}
